import React from 'react'
import { useIntl } from 'react-intl'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { submitForm, SubmitFormItem } from '../../../../util'
import { Action } from '../../../../legacy-design-system/action'
import { useSplitClient } from 'contrastBase/state'
import { BulkRouteActionByFiltersArgs } from '../../api/shared'
import {
  IconDownload,
  IconSize
} from '@contrast-security-inc/design-system-foundations'
import { useMutateExportRoutes } from '../../api/route-export-async'
export const exportActionTestId = 'export-action-test-id'

interface ExportActionBaseProps {
  organizationUuid: string
  applicationUuid: string
}

interface ExportByFiltersActionProps extends ExportActionBaseProps {
  excludedHashes: string[]
  filters: {
    filterText: string
    quickFilter: string
    startDate?: number
    endDate?: number
    discoveredStartDate?: number
    discoveredEndDate?: number
    agentSessionId?: string[]
    applications?: string[]
  }
}

interface ExportByRouteHashesActionProps extends ExportActionBaseProps {
  routeHashes: string[]
  filters: BulkRouteActionByFiltersArgs['filters']
}

interface BuildRouteExportUrlParameters extends ExportActionBaseProps {
  withFilters?: boolean
  agentSessionId?: string[] | string
}

function buildRouteExportUrl({
  organizationUuid,
  applicationUuid,
  withFilters,
  agentSessionId
}: BuildRouteExportUrlParameters) {
  return `${organizationUuid}/applications/${applicationUuid}/route/bulk/export/${
    withFilters ? 'filters/' : ''
  }csv${
    agentSessionId && agentSessionId !== undefined
      ? '?agentSessionId=' + agentSessionId
      : ''
  }`
}

export const buildFormDataWithFilters: (
  props: ExportByFiltersActionProps
) => SubmitFormItem[] = ({
  filters: {
    filterText,
    quickFilter,
    startDate,
    endDate,
    discoveredStartDate,
    discoveredEndDate,
    applications
  },
  excludedHashes
}) => {
  return [
    { name: 'filterText', value: filterText },
    { name: 'quickFilter', value: quickFilter },
    {
      name: 'startDate',
      value: typeof startDate === 'undefined' ? '' : startDate.toString()
    },
    {
      name: 'endDate',
      value: typeof endDate === 'undefined' ? '' : endDate.toString()
    },
    {
      name: 'discoveredStartDate',
      value:
        typeof discoveredStartDate === 'undefined'
          ? ''
          : discoveredStartDate.toString()
    },
    {
      name: 'discoveredEndDate',
      value:
        typeof discoveredEndDate === 'undefined'
          ? ''
          : discoveredEndDate.toString()
    },
    ...(applications
      ? applications.map((value) => ({
          name: 'applicationIds',
          value
        }))
      : []),
    ...excludedHashes.map((value) => ({
      name: 'excludedHashes',
      value
    }))
  ]
}
export const convertPayloadToJson = (payload) =>
  payload.reduce((acc, it) => ({ ...acc, [it.name]: it.value }), {})

export function ExportByFiltersAction({
  organizationUuid,
  applicationUuid,
  ...props
}: ExportByFiltersActionProps) {
  const intl = useIntl()
  const { filters } = props
  const { agentSessionId } = filters
  const exportUrl = buildRouteExportUrl({
    organizationUuid,
    applicationUuid,
    withFilters: true,
    agentSessionId
  })
  const exportData = [
    ...buildFormDataWithFilters({
      organizationUuid,
      applicationUuid,
      ...props
    })
  ]
  const isFlagOn = useSplitClient().isSplitTreatmentOn(
    'AS-4_async_route_coverage_file_creation'
  )
  const onSuccess = React.useCallback(() => {
    toast(
      intl.formatMessage({
        id: 'detail_attestationReport_successfullyReportInitialization'
      }),
      { variant: 'confirmation' }
    )
  }, [intl])
  const onError = React.useCallback(() => {
    toast(
      intl.formatMessage({
        id: 'detail_attestationReport_failedReportInitialization'
      }),
      { variant: 'error' }
    )
  }, [intl])
  const { mutate } = useMutateExportRoutes({ onSuccess, onError })

  return (
    <ExportAction
      onClick={() =>
        isFlagOn
          ? // @ts-ignore
            mutate({
              orgUuid: organizationUuid,
              appUuid: applicationUuid,
              payload: {
                ...convertPayloadToJson(exportData),
                agentSessionId
              }
            })
          : submitForm(exportUrl, exportData)
      }
    />
  )
}

export function ExportByRouteHashesAction({
  organizationUuid,
  applicationUuid,
  routeHashes,
  filters,
  ...rest
}: ExportByRouteHashesActionProps) {
  const formData = routeHashes.map((value) => ({
    name: 'routeHashes',
    value
  }))
  const agentSessionId = filters.agentSessionId
    ? filters.agentSessionId
    : undefined

  return (
    <ExportAction
      {...rest}
      onClick={() => {
        submitForm(
          buildRouteExportUrl({
            organizationUuid,
            applicationUuid,
            agentSessionId
          }),
          [...formData]
        )
      }}
    />
  )
}

function ExportAction({
  onClick,
  size = 'md'
}: {
  onClick: () => void
  size?: IconSize
}) {
  return (
    <Action
      size={size}
      data-testid={exportActionTestId}
      icon={IconDownload}
      iconTooltipMessage={{ id: 'action_export_data' }}
      onClick={onClick}
    />
  )
}
